// src/lib/phoneRules.js — per-dial-code phone SHAPE rules, shared by the agent
// Desk and the PUBLIC fill form.
//
// ⚠ This module is imported by src/fill/* — keep it dependency-free (vite.config:
// "fill/* must not import authed code"). No i18n here on purpose: the rule is one
// fact, each surface phrases it in its own dictionary.
//
// Why it exists (measured 2026-07-20): the fill form shipped with no phone check
// and 2 of its first 13 submissions minted undialable parties — +995 with 8
// national digits (ჯილდა, 2026-07-20) and with 7 (გიო, 2026-07-16). Those are the
// ONLY two malformed Georgian numbers among 2,414 party phones, i.e. the public
// form is the sole producer. The agent finds out when the call fails, and core's
// party matching is exact-digit (normalize_phone → find_party_candidates), so a
// wrong number can't match the person's existing row either.
//
// This VALIDATES ONLY — it never rewrites what someone typed. A silently
// "corrected" phone number is an invented observation.

// cc → [minDigits, maxDigits, leadingPattern?] of the NATIONAL part (dial code
// excluded). Only add a row you are SURE of: a wrong row blocks a real client on
// a public form where nobody is there to help them past it. Anything not listed
// falls through to GENERIC, which rejects only lengths no phone number has.
const RULES = {
  // ⚠ +995 is the only code with a leading pattern, and both surfaces phrase
  // reason:'lead' as a Georgian-specific sentence. Give another country one and
  // you owe it its own message first.
  '+995': [9, 9, /^[53]/],  // GE — mobile 5XX XXX XXX, landline 3XX XXX XXX
  '+374': [8, 8],           // AM
  '+994': [9, 9],           // AZ
  '+90':  [10, 10],         // TR
  '+7':   [10, 10],         // RU / KZ
  '+380': [9, 9],           // UA
  '+375': [9, 9],           // BY
  '+998': [9, 9],           // UZ
  '+972': [8, 9],           // IL — landline 8, mobile 9
  '+971': [8, 9],           // AE
  '+966': [9, 9],           // SA
  '+974': [8, 8],           // QA
  '+98':  [10, 10],         // IR
  '+91':  [10, 10],         // IN
  '+1':   [10, 10],         // US / CA
  '+44':  [9, 10],          // GB
  '+48':  [9, 9],           // PL
  '+33':  [9, 9],           // FR
  '+34':  [9, 9],           // ES
  '+351': [9, 9],           // PT
  '+30':  [10, 10],         // GR
  '+420': [9, 9],           // CZ
  '+45':  [8, 8],           // DK
  '+47':  [8, 8],           // NO
  '+370': [8, 8],           // LT
  '+371': [8, 8],           // LV
  '+40':  [9, 9],           // RO
  '+31':  [9, 9],           // NL
  '+41':  [9, 9],           // CH
  // Deliberately unlisted (their national lengths genuinely vary — a range wide
  // enough to be correct would catch nothing GENERIC doesn't): +49 DE, +39 IT,
  // +43 AT, +46 SE, +358 FI, +353 IE, +372 EE, +359 BG, +36 HU, +32 BE, +86 CN.
}
// E.164 allows 15 digits including the country code, so 14 is the widest a
// national part can be; nothing real is shorter than 5.
const GENERIC = [5, 14]

const CODES_LONGEST_FIRST = Object.keys(RULES).sort((a, b) => b.length - a.length)

// Split a typed number into [dialCode, nationalDigits]. A leading '+' wins over
// the fallback (someone pasting a full international number). Returns a null code
// when the pasted prefix isn't one we hold a rule for — unknown country means we
// judge it by GENERIC only, never by the fallback's rule.
export function splitDial(raw, fallbackCc = '+995') {
  const s = String(raw || '').trim()
  if (!s.startsWith('+')) return [fallbackCc, s.replace(/\D/g, '')]
  const hit = CODES_LONGEST_FIRST.find(c => s.startsWith(c))
  return hit ? [hit, s.slice(hit.length).replace(/\D/g, '')] : [null, s.replace(/\D/g, '')]
}

// null = nothing to complain about (including an EMPTY number — whether a phone
// is REQUIRED is each surface's own policy, not a shape question).
// Otherwise { reason: 'len' | 'lead', cc, want, got }.
export function checkPhone(cc, national) {
  const got = String(national || '').replace(/\D/g, '')
  if (!got) return null
  const [min, max, lead] = RULES[cc] || GENERIC
  const want = min === max ? String(min) : `${min}–${max}`
  if (got.length < min || got.length > max) return { reason: 'len', cc, want, got: got.length }
  if (lead && !lead.test(got)) return { reason: 'lead', cc, want, got: got.length }
  return null
}
